// The 15 OddsJam Gap specifications.
//
// Source of truth: STRIKZA_OddsJam_Gaps_v1.0.docx (May 2026).
// Doctrinal posture: every entry here is a build-spec'd feature that
// STRIKZA ships at the indicated tier + phase. The landing surfaces
// this as the Feature Matrix and the per-tier feature lists. The
// dataset is intentionally read-only on the marketing surface — when
// a gap is delivered, status flips here, not in the page copy.

export type Big6Pillar = "P1" | "P2" | "P3" | "P4" | "P5" | "P6";

// Tier gates. "All Tiers" means the feature is included on every
// paid tier and the doctrinal Free tier (when introduced).
export type TierGate = "All Tiers" | "Sika Pro+" | "Pro+" | "Elite+";

// Build phases per the doctrine:
//   - Pre-Launch: shipping before the World Cup 2026 pilot window
//   - Phase 1:    first 6 months post-launch
//   - Phase 2:    expansion tier (Year 2)
export type BuildPhase = "Pre-Launch" | "Phase 1" | "Phase 2";

export interface OddsJamGap {
  readonly id: string;
  readonly name: string;
  readonly tagline: string;
  readonly pillars: readonly Big6Pillar[];
  readonly tier: TierGate;
  readonly phase: BuildPhase;
}

export const ODDSJAM_GAPS: readonly OddsJamGap[] = [
  {
    id: "GAP-01",
    name: "Real-Time Odds Comparison Screen",
    tagline:
      "One screen, every tracked book, every active fixture — best price highlighted, vig surfaced, drift annotated.",
    pillars: ["P1", "P5"],
    tier: "Sika Pro+",
    phase: "Pre-Launch",
  },
  {
    id: "GAP-02",
    name: "No-Vig Fair Odds Display",
    tagline:
      "Strip the bookmaker's margin off every market and show what a fair coin-flip line would actually be.",
    pillars: ["P1"],
    tier: "All Tiers",
    phase: "Pre-Launch",
  },
  {
    id: "GAP-03",
    name: "Kelly Criterion Auto-Sizing",
    tagline:
      "Recommended stake per leg, sized by edge × bankroll × correlation. Cap at fractional Kelly. No 'feeling lucky' mode.",
    pillars: ["P4"],
    tier: "Sika Pro+",
    phase: "Pre-Launch",
  },
  {
    id: "GAP-04",
    name: "Expected Value Display per Signal",
    tagline:
      "Every signal carries an EV% — the gap between STRIKZA's Fair Price probability and the book's price.",
    pillars: ["P1", "P2"],
    tier: "All Tiers",
    phase: "Pre-Launch",
  },
  {
    id: "GAP-05",
    name: "Poisson Model Transparency Layer",
    tagline:
      "Open the Fair Price Engine. See the inputs, the Poisson outputs, the confidence — STRIKZA shows its homework on every fixture.",
    pillars: ["P1"],
    tier: "Sika Pro+",
    phase: "Pre-Launch",
  },
  {
    id: "GAP-06",
    name: "Historical Odds + Signal Database",
    tagline:
      "Every signal, every odds snapshot, every settlement — backtestable, queryable, never overwritten. The data flywheel.",
    pillars: ["P6"],
    tier: "Pro+",
    phase: "Pre-Launch",
  },
  {
    id: "GAP-07",
    name: "Line Movement Alert System",
    tagline:
      "Steam moves, public drift, trap lines — push notifications classify the move and tell you whether to chase or fade.",
    pillars: ["P2", "P3"],
    tier: "Pro+",
    phase: "Phase 1",
  },
  {
    id: "GAP-08",
    name: "Personal CLV Dashboard",
    tagline:
      "Your bets, your closing-line value, your Brier score, your market mix. Private. Beats win-rate as a skill measure.",
    pillars: ["P2"],
    tier: "Sika Pro+",
    phase: "Phase 1",
  },
  {
    id: "GAP-09",
    name: "STRIKZA Verified Return Certificate",
    tagline:
      "Time-stamped, tamper-evident, third-party-audited record of every signal STRIKZA published. Public within 4 months of launch.",
    pillars: ["P6"],
    tier: "All Tiers",
    phase: "Phase 1",
  },
  {
    id: "GAP-10",
    name: "Slip Autopsy",
    tagline:
      "Lost a slip? Get an honest post-mortem on which agent's call held, which broke, what the closing line said, what we'd do differently.",
    pillars: ["P1", "P3"],
    tier: "Sika Pro+",
    phase: "Phase 1",
  },
  {
    id: "GAP-11",
    name: "Promo & Bonus Harvesting Engine",
    tagline:
      "STRIKZA tracks every active promo across the bookmaker stack and tells you which one to claim — with the EV math attached.",
    pillars: ["P5"],
    tier: "Elite+",
    phase: "Phase 1",
  },
  {
    id: "GAP-12",
    name: "West African Arbitrage Scanner",
    tagline:
      "Cross-book arbitrage detection scoped to Stake / Betway / SportyBet / 1xBet — fully sized, fully timed, never auto-placed.",
    pillars: ["P5", "P4"],
    tier: "Elite+",
    phase: "Phase 2",
  },
  {
    id: "GAP-13",
    name: "Middling Detection Engine",
    tagline:
      "Catch the rare middle: when two books drift far enough apart that both bets can win, with disciplined sizing on each side.",
    pillars: ["P3", "P4"],
    tier: "Elite+",
    phase: "Phase 2",
  },
  {
    id: "GAP-14",
    name: "Free Daily Signal Distribution",
    tagline:
      "One free signal every day, every tier — including the doctrinal Free tier. Every signal is logged to the ledger like any other.",
    pillars: ["P2"],
    tier: "All Tiers",
    phase: "Pre-Launch",
  },
  {
    id: "GAP-15",
    name: "Structured Onboarding Architecture",
    tagline:
      "Five-question intake → Bettor Archetype → Sika starter goal → first slip in under three minutes. Mandatory Safe Mode config.",
    pillars: ["P4"],
    tier: "All Tiers",
    phase: "Pre-Launch",
  },
];

// Helper: features included for a given tier (cumulative).
// Sika Pro+ inherits All Tiers; Pro+ inherits Sika Pro+; Elite+ inherits Pro+.
export const TIER_INHERITANCE: { readonly [k in TierGate]: readonly TierGate[] } = {
  "All Tiers": ["All Tiers"],
  "Sika Pro+": ["All Tiers", "Sika Pro+"],
  "Pro+": ["All Tiers", "Sika Pro+", "Pro+"],
  "Elite+": ["All Tiers", "Sika Pro+", "Pro+", "Elite+"],
};

export function gapsForTier(tier: TierGate): readonly OddsJamGap[] {
  const allowed = TIER_INHERITANCE[tier];
  return ODDSJAM_GAPS.filter((g) => allowed.includes(g.tier));
}

// Verified Return Certificate timeline — single source of truth.
// Used by Hero, StatsSection, LedgerSection, FeatureMatrix.
export const VERIFIED_RETURN_DAYS = 90 as const;
export const VERIFIED_RETURN_MONTHS = 4 as const;
export const VERIFIED_RETURN_BLURB =
  "STRIKZA publishes a verified 90-day CLV+ record within 4 months of launch.";
